import React, { useState, useEffect } from 'react';
import { Search, Edit2, Shield, Users, UserCheck, Mail, Calendar, X, Check, Save, UserPlus, ChevronDown } from 'lucide-react';
import Swal from 'sweetalert2';
import config from '../config';

const ROLES = [
  { label: 'Superadmin', value: 'Super_Admin' },
  { label: 'Admin',      value: 'Admin' },
  { label: 'Staff',      value: 'Staff' },
  { label: 'Developer',  value: 'Developer' },
  { label: 'Marketing',  value: 'Marketing' },
];

const UserAdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortField, setSortField] = useState('registrationDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterRole, setFilterRole] = useState('all');
  const [filterApproved, setFilterApproved] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const [newAdmin, setNewAdmin] = useState({
    username: '', fullNames: '', email: '', password: '', role: '', team: '',
  });

  const API_URL = `${config.API_BASE_URL}/Admin`;
  const TEAMS_URL = `http://localhost:7057/api/Teams`;

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL, { headers: { 'accept': 'text/plain' } });
      if (response.ok) setAdmins(await response.json());
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch admin users', background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#3b82f6' });
    }
    setLoading(false);
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch(TEAMS_URL, { headers: { 'accept': 'application/json' } });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  useEffect(() => { fetchAdmins(); fetchTeams(); }, []);

  const openEditModal = (admin) => { setEditingAdmin({ ...admin }); setShowEditModal(true); };
  const closeEditModal = () => { setShowEditModal(false); setEditingAdmin(null); };
  const handleInputChange = (field, value) => setEditingAdmin({ ...editingAdmin, [field]: value });

  const openAddModal = () => {
    setNewAdmin({ username: '', fullNames: '', email: '', password: '', role: '', team: '' });
    setShowAddModal(true);
  };
  const closeAddModal = () => setShowAddModal(false);
  const handleNewAdminChange = (field, value) => setNewAdmin({ ...newAdmin, [field]: value });

const addAdmin = async () => {
    if (!newAdmin.username || !newAdmin.password || !newAdmin.fullNames || !newAdmin.email || !newAdmin.role) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Username, Full Name, Email, and Password are required.', background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#3b82f6' });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('username', newAdmin.username);
      formData.append('fullNames', newAdmin.fullNames);
      formData.append('email', newAdmin.email);
      formData.append('password', newAdmin.password);
      formData.append('role', newAdmin.role);
      if (newAdmin.team) formData.append('team', newAdmin.team);
      // Auto-grant addAdminPermission for Superadmin
      if (newAdmin.role === 'Super_Admin') formData.append('addAdminPermission', 'true');

      const response = await fetch(`${API_URL}/add-new-admin`, {
        method: 'POST',
        headers: { 'accept': 'text/plain' },
        body: formData,
      });
      if (response.ok) {
        await fetchAdmins();
        closeAddModal();
        Swal.fire({ icon: 'success', title: 'Admin Added!', text: `${newAdmin.fullNames} has been added successfully.`, background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#10b981' });
      } else {
        const errText = await response.text();
        Swal.fire({ icon: 'error', title: `Error ${response.status}`, text: errText || 'Failed to add admin.', background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#ef4444' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to add admin user.', background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#ef4444' });
    }
  };

  const updateAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/${editingAdmin.username}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'accept': 'text/plain' },
        body: JSON.stringify(editingAdmin)
      });
      if (response.ok) {
        await fetchAdmins();
        closeEditModal();
        Swal.fire({ icon: 'success', title: 'Updated!', text: 'Admin user updated successfully.', background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#10b981' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update admin user.', background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#ef4444' });
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch =
      admin.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.fullNames?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.role?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || admin.role === filterRole;
    const matchesApproved = filterApproved === 'all' ||
      (filterApproved === 'approved' && admin.isApproved) ||
      (filterApproved === 'pending' && !admin.isApproved);
    return matchesSearch && matchesRole && matchesApproved;
  });

  const sortedAdmins = [...filteredAdmins].sort((a, b) => {
    let aVal = a[sortField], bVal = b[sortField];
    if (sortField === 'registrationDate') { aVal = new Date(aVal).getTime(); bVal = new Date(bVal).getTime(); }
    else if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal?.toLowerCase(); }
    return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const totalPages = Math.ceil(sortedAdmins.length / itemsPerPage);
  const currentAdmins = sortedAdmins.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const uniqueRoles = [...new Set(admins.map(a => a.role))].filter(Boolean);

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: '2rem', fontFamily: '"Inter", -apple-system, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        .admin-card { background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); border: 1px solid #334155; border-radius: 16px; padding: 1.5rem; transition: all 0.3s ease; position: relative; overflow: hidden; }
        .admin-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, #3b82f6, #8b5cf6); }
        .admin-card:hover { transform: translateY(-4px); border-color: #3b82f6; box-shadow: 0 8px 24px rgba(59,130,246,0.2); }
        .search-input { width: 100%; padding: 0.875rem 1rem 0.875rem 3rem; background: #1e293b; border: 1px solid #334155; border-radius: 12px; color: #f1f5f9; font-size: 0.95rem; transition: all 0.3s ease; box-sizing: border-box; }
        .search-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        .search-input::placeholder { color: #64748b; }
        .btn { padding: 0.75rem 1.5rem; border-radius: 10px; border: none; font-weight: 500; cursor: pointer; transition: all 0.3s ease; display: inline-flex; align-items: center; gap: 0.5rem; font-family: 'Inter', sans-serif; font-size: 0.9rem; }
        .btn-add { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; }
        .btn-add:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(59,130,246,0.4); }
        .btn-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; }
        .btn-success:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(16,185,129,0.4); }
        .btn-secondary { background: #334155; color: #f1f5f9; }
        .btn-secondary:hover { background: #475569; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; animation: fadeIn 0.2s ease; padding: 1rem; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .modal-content { background: #1e293b; border: 1px solid #334155; border-radius: 16px; max-width: 800px; width: 100%; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s ease; }
        .modal-content-sm { background: #1e293b; border: 1px solid #334155; border-radius: 16px; max-width: 500px; width: 100%; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s ease; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .input-field { width: 100%; padding: 0.75rem; background: #0f172a; border: 1px solid #334155; border-radius: 8px; color: #f1f5f9; font-size: 0.9rem; transition: all 0.3s ease; box-sizing: border-box; }
        .input-field:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        .input-field::placeholder { color: #475569; }
        .select-input { width: 100%; padding: 0.75rem 2.5rem 0.75rem 0.75rem; background: #0f172a; border: 1px solid #334155; border-radius: 8px; color: #f1f5f9; font-size: 0.9rem; transition: all 0.3s ease; box-sizing: border-box; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 9L1 4h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.875rem center; cursor: pointer; font-family: 'Inter', sans-serif; }
        .select-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        .select-input option { background: #1e293b; color: #f1f5f9; }
        label { display: block; margin-bottom: 0.4rem; color: #94a3b8; font-weight: 500; font-size: 0.85rem; }
        .badge { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.375rem 0.75rem; border-radius: 6px; font-size: 0.85rem; font-weight: 500; }
        .badge-primary { background: rgba(59,130,246,0.1); color: #60a5fa; border: 1px solid rgba(59,130,246,0.2); }
        .badge-success { background: rgba(16,185,129,0.1); color: #34d399; border: 1px solid rgba(16,185,129,0.2); }
        .badge-warning { background: rgba(245,158,11,0.1); color: #fbbf24; border: 1px solid rgba(245,158,11,0.2); }
        .permission-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; margin-top: 1rem; }
        .permission-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: #0f172a; border: 1px solid #334155; border-radius: 8px; }
        .checkbox { width: 20px; height: 20px; cursor: pointer; accent-color: #3b82f6; }
        .spinner { border: 3px solid #334155; border-top: 3px solid #3b82f6; border-radius: 50%; width: 40px; height: 40px; animation: spin 0.8s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .page-btn { padding: 0.5rem 0.875rem; background: #1e293b; border: 1px solid #334155; border-radius: 8px; color: #f1f5f9; cursor: pointer; transition: all 0.2s ease; font-size: 0.9rem; font-family: 'Inter', sans-serif; min-width: 40px; }
        .page-btn:hover:not(:disabled) { background: #334155; border-color: #3b82f6; }
        .page-btn.active { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); border-color: #3b82f6; color: white; }
        .page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .select-field { padding: 0.625rem 2.5rem 0.625rem 1rem; background: #1e293b; border: 1px solid #334155; border-radius: 8px; color: #f1f5f9; font-size: 0.9rem; cursor: pointer; font-family: 'Inter', sans-serif; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23f1f5f9' d='M6 9L1 4h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.875rem center; }
        .select-field:focus { outline: none; border-color: #3b82f6; }
        .sort-button { background: rgba(59,130,246,0.1); border: 1px solid rgba(59,130,246,0.2); color: #60a5fa; padding: 0.5rem 1rem; border-radius: 8px; cursor: pointer; font-size: 0.85rem; display: inline-flex; align-items: center; gap: 0.5rem; transition: all 0.2s ease; }
        .sort-button:hover { background: rgba(59,130,246,0.2); }
        .req { color: #f87171; margin-left: 2px; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #1e293b; }
        ::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
        .teams-loading { color: #64748b; font-size: 0.8rem; font-style: italic; padding: 0.75rem; }
      `}</style>

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2.5rem', fontWeight: '700', background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
              User Admin Management
            </h1>
            <p style={{ color: '#64748b', fontSize: '1.05rem' }}>Manage administrator accounts and permissions</p>
          </div>
          <button onClick={openAddModal} className="btn btn-add" style={{ alignSelf: 'center' }}>
            <UserPlus size={18} />
            Add Admin
          </button>
        </div>

        {/* Search & Filters */}
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ position: 'relative', maxWidth: '500px', marginBottom: '1rem' }}>
            <Search size={20} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input type="text" placeholder="Search by username, name, email, or role..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div><label>Filter by Role</label><select value={filterRole} onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }} className="select-field"><option value="all">All Roles</option>{uniqueRoles.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
            <div><label>Filter by Status</label><select value={filterApproved} onChange={(e) => { setFilterApproved(e.target.value); setCurrentPage(1); }} className="select-field"><option value="all">All Status</option><option value="approved">Approved</option><option value="pending">Pending</option></select></div>
            <div><label>Sort by</label><select value={sortField} onChange={(e) => setSortField(e.target.value)} className="select-field"><option value="registrationDate">Registration Date</option><option value="fullNames">Name</option><option value="username">Username</option><option value="role">Role</option><option value="email">Email</option></select></div>
            <div><label>Order</label><button onClick={() => setSortDirection(s => s === 'asc' ? 'desc' : 'asc')} className="sort-button" style={{ width: '100%', justifyContent: 'center' }}>{sortDirection === 'asc' ? '↑ Ascending' : '↓ Descending'}</button></div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div className="badge badge-primary"><Users size={16} />Total: {admins.length}</div>
            <div className="badge badge-success"><UserCheck size={16} />Showing: {currentAdmins.length}</div>
            <div className="badge badge-primary">Page {currentPage} of {totalPages || 1}</div>
          </div>
        </div>

        {/* Cards */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem', gap: '1rem' }}>
            <div className="spinner"></div><p style={{ color: '#64748b' }}>Loading admins...</p>
          </div>
        ) : sortedAdmins.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', background: '#1e293b', border: '1px solid #334155', borderRadius: '16px' }}>
            <p style={{ color: '#64748b', fontSize: '1.1rem' }}>{searchTerm || filterRole !== 'all' || filterApproved !== 'all' ? 'No admins found matching your filters.' : 'No admin users found.'}</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              {currentAdmins.map((admin) => (
                <div key={admin.username} className="admin-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      {admin.profilePicture ? (
                        <img src={`data:image/jpeg;base64,${admin.profilePicture}`} alt={admin.fullNames} style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: '60px', height: '60px', borderRadius: '12px', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: '600', color: 'white', flexShrink: 0 }}>
                          {admin.fullNames?.[0]?.toUpperCase() || 'A'}
                        </div>
                      )}
                      <div>
                        <h3 style={{ color: '#f1f5f9', fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.25rem' }}>{admin.fullNames}</h3>
                        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>@{admin.username}</p>
                      </div>
                    </div>
                    <button onClick={() => openEditModal(admin)} style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px', padding: '0.5rem', cursor: 'pointer', color: '#60a5fa', transition: 'all 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59,130,246,0.2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}>
                      <Edit2 size={18} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Shield size={16} color="#8b5cf6" /><span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Role:</span><span style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: '500' }}>{admin.role}</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Mail size={16} color="#3b82f6" /><span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Email:</span><span style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>{admin.email}</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Calendar size={16} color="#10b981" /><span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Registered:</span><span style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>{new Date(admin.registrationDate).toLocaleDateString()}</span></div>
                    {admin.team && <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Users size={16} color="#f59e0b" /><span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Team:</span><span style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>{admin.team}</span></div>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '1rem', borderTop: '1px solid #334155' }}>
                    {admin.isApproved && <span className="badge badge-success"><Check size={14} />Approved</span>}
                    {admin.addAdminPermission && <span className="badge badge-warning"><Shield size={14} />Admin Access</span>}
                    <span className="badge badge-primary">ID: {admin.identificationCode}</span>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="page-btn">First</button>
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="page-btn">Previous</button>
                {[...Array(totalPages)].map((_, i) => {
                  const p = i + 1;
                  if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1))
                    return <button key={p} onClick={() => setCurrentPage(p)} className={`page-btn ${currentPage === p ? 'active' : ''}`}>{p}</button>;
                  else if (p === currentPage - 2 || p === currentPage + 2)
                    return <span key={p} style={{ color: '#64748b' }}>...</span>;
                  return null;
                })}
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="page-btn">Next</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="page-btn">Last</button>
              </div>
            )}
          </>
        )}

        {/* ── ADD ADMIN MODAL ── */}
        {showAddModal && (
          <div className="modal-overlay" onClick={closeAddModal}>
            <div className="modal-content-sm" onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserPlus size={20} color="white" />
                  </div>
                  <div>
                    <h2 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#f1f5f9', margin: 0 }}>Add New Admin</h2>
                    <p style={{ color: '#64748b', fontSize: '0.8rem', margin: 0 }}>Fill in the required fields</p>
                  </div>
                </div>
                <button onClick={closeAddModal} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0.5rem' }}><X size={22} /></button>
              </div>

              <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Username */}
                <div>
                  <label>Username<span className="req">*</span></label>
                  <input type="text" placeholder="e.g. john_doe" value={newAdmin.username} onChange={(e) => handleNewAdminChange('username', e.target.value)} className="input-field" />
                </div>

                {/* Full Name */}
                <div>
                  <label>Full Name<span className="req">*</span></label>
                  <input type="text" placeholder="e.g. John Doe" value={newAdmin.fullNames} onChange={(e) => handleNewAdminChange('fullNames', e.target.value)} className="input-field" />
                </div>

                {/* Email */}
                <div>
                  <label>Email<span className="req">*</span></label>
                  <input type="email" placeholder="e.g. john@company.com" value={newAdmin.email} onChange={(e) => handleNewAdminChange('email', e.target.value)} className="input-field" />
                </div>

                {/* Password */}
                <div>
                  <label>Password<span className="req">*</span></label>
                  <input type="password" placeholder="Enter password" value={newAdmin.password} onChange={(e) => handleNewAdminChange('password', e.target.value)} className="input-field" />
                </div>

                {/* Role — dropdown */}
                <div>
                  <label>Role</label>
                  <select
                    value={newAdmin.role}
                    onChange={(e) => handleNewAdminChange('role', e.target.value)}
                    className="select-input"
                  >
                    <option value="">— Select Role —</option>
                    {ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {/* Team — dropdown from API */}
                <div>
                  <label>Team</label>
                  <select
                    value={newAdmin.team}
                    onChange={(e) => handleNewAdminChange('team', e.target.value)}
                    className="select-input"
                  >
                    <option value="">— Select Team —</option>
                    {teams.length === 0 ? (
                      <option disabled>Loading teams...</option>
                    ) : (
                      teams.map(t => (
                        <option key={t.id} value={t.teamName}>{t.teamName}</option>
                      ))
                    )}
                  </select>
                  {teams.length === 0 && (
                    <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.4rem', fontStyle: 'italic' }}>
                      Fetching teams from server...
                    </p>
                  )}
                </div>

                <p style={{ color: '#475569', fontSize: '0.8rem', margin: 0 }}><span className="req">*</span> Required fields</p>
              </div>

              <div style={{ padding: '1rem 2rem 1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: '1px solid #334155' }}>
                <button onClick={closeAddModal} className="btn btn-secondary">Cancel</button>
                <button onClick={addAdmin} className="btn btn-add"><UserPlus size={16} />Add Admin</button>
              </div>
            </div>
          </div>
        )}

        {/* ── EDIT MODAL ── */}
        {showEditModal && editingAdmin && (
          <div className="modal-overlay" onClick={closeEditModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '2rem', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '1.75rem', fontWeight: '600', color: '#f1f5f9' }}>Edit Admin User</h2>
                <button onClick={closeEditModal} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0.5rem' }}><X size={24} /></button>
              </div>
              <div style={{ padding: '2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
                  <div><label>Username</label><input type="text" value={editingAdmin.username} disabled className="input-field" style={{ opacity: 0.6, cursor: 'not-allowed' }} /></div>
                  <div><label>Full Name</label><input type="text" value={editingAdmin.fullNames} onChange={(e) => handleInputChange('fullNames', e.target.value)} className="input-field" /></div>
                  <div><label>Email</label><input type="email" value={editingAdmin.email} onChange={(e) => handleInputChange('email', e.target.value)} className="input-field" /></div>

                  {/* Role dropdown in edit modal */}
                  <div>
                    <label>Role</label>
                    <select value={editingAdmin.role} onChange={(e) => handleInputChange('role', e.target.value)} className="select-input">
                      <option value="">— Select Role —</option>
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>

                  {/* Team dropdown in edit modal */}
                  <div>
                    <label>Team</label>
                    <select value={editingAdmin.team || ''} onChange={(e) => handleInputChange('team', e.target.value)} className="select-input">
                      <option value="">— Select Team —</option>
                      {teams.map(t => <option key={t.id} value={t.teamName}>{t.teamName}</option>)}
                    </select>
                  </div>

                  <div><label>Password</label><input type="password" value={editingAdmin.password} onChange={(e) => handleInputChange('password', e.target.value)} className="input-field" /></div>
                </div>
                <div style={{ marginTop: '2rem' }}>
                  <h3 style={{ color: '#f1f5f9', fontSize: '1.1rem', marginBottom: '1rem', fontWeight: '600' }}>Permissions</h3>
                  <div className="permission-grid">
                    {[
                      { key: 'chatPermission', label: 'Chat' }, { key: 'notificationPermission', label: 'Notifications' },
                      { key: 'addUserPermission', label: 'Add User' }, { key: 'addAdminPermission', label: 'Add Admin' },
                      { key: 'taskPermission', label: 'Tasks' }, { key: 'calendarPermission', label: 'Calendar' },
                      { key: 'incidentPermission', label: 'Incidents' }, { key: 'ticketlistPermission', label: 'Tickets' },
                      { key: 'galleryPermission', label: 'Gallery' }, { key: 'workloadPermission', label: 'Workload' },
                      { key: 'emailPermission', label: 'Email' }, { key: 'oncallPermission', label: 'On-Call' },
                      { key: 'isApproved', label: 'Approved' }
                    ].map((perm) => (
                      <div key={perm.key} className="permission-item">
                        <input type="checkbox" checked={editingAdmin[perm.key] || false} onChange={(e) => handleInputChange(perm.key, e.target.checked)} className="checkbox" />
                        <span style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>{perm.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ padding: '1.5rem 2rem', borderTop: '1px solid #334155', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button onClick={closeEditModal} className="btn btn-secondary">Cancel</button>
                <button onClick={updateAdmin} className="btn btn-success"><Save size={18} />Save Changes</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default UserAdminManagement;